import { useEffect, useMemo, useState } from "react";
import {
  changePassword,
  createOwnerAccount,
  createOwnerOrganization,
  deleteOwnerOrganization,
  getOwnerOrganization,
  getOwnerDiagnostics,
  getOwnerPlatformStatus,
  getOwnerReconciliation,
  listOwnerAccounts,
  listOwnerOrganizations,
  listOwnerCheckoutSessions,
  listOwnerEmailDeliveries,
  listOwnerRecentOrders,
  listOwnerScannerActivity,
  listOwnerWebhooks,
  updateOwnerAccount,
  updateOwnerOrganization,
  removeOwnerOrganizationMember,
  setOwnerOrganizationMember,
  updateOwnerOrganizationMember
} from "../lib/api";
import type {
  ManagedAccountRole,
  OwnerAccount,
  OwnerAccountCreateInput,
  OwnerCheckoutSession,
  OwnerDiagnostics,
  OwnerEmailDelivery,
  OwnerRecentOrder,
  OwnerReconciliationReport,
  OwnerScannerActivity,
  OwnerWebhookEvent,
  OwnerOrganizationDetail,
  OwnerOrganizationInput,
  OwnerOrganizationSummary,
  PlatformStatus
} from "../lib/types";
import { FormField, MetricCard, PageShell, Panel, StatusBadge } from "../components/admin-ui";
import WorkspaceSwitcher from "../components/WorkspaceSwitcher";
import type { WorkspaceNavigationAccess } from "../lib/workspace-navigation";

interface OwnerDashboardProps {
  navigate: (path: string) => void;
  path: string;
  workspaceNavigationAccess: WorkspaceNavigationAccess;
}

type OwnerSection = "health" | "accounts" | "organizations" | "orders" | "email-deliveries" | "scanner-activity" | "change-password";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const navItems: Array<{ key: OwnerSection; label: string; path: string }> = [
  { key: "health", label: "Health", path: "/owner" },
  { key: "accounts", label: "Accounts", path: "/owner/accounts" },
  { key: "organizations", label: "Organizations", path: "/owner/organizations" },
  { key: "scanner-activity", label: "Scanner Activity", path: "/owner/scanner-activity" },
  { key: "orders", label: "Orders", path: "/owner/orders" },
  { key: "email-deliveries", label: "Email Deliveries", path: "/owner/email-deliveries" },
  { key: "change-password", label: "Change Password", path: "/owner/change-password" }
];

const defaultCreateAccount: OwnerAccountCreateInput = {
  name: "",
  email: "",
  temporaryPassword: "",
  role: "promoter_organizer",
  isActive: true,
  confirmOwnerCreation: false
};

function sectionForPath(path: string): OwnerSection {
  if (path === "/owner/accounts") return "accounts";
  if (path === "/owner/organizations") return "organizations";
  if (path === "/owner/orders") return "orders";
  if (path === "/owner/email-deliveries") return "email-deliveries";
  if (path === "/owner/scanner-activity") return "scanner-activity";
  if (path === "/owner/change-password") return "change-password";
  return "health";
}

function formatDateTime(value?: string): string {
  if (!value) return "Not yet";
  return new Date(value).toLocaleString();
}

function badgeTone(label: string): "success" | "warning" | "danger" | "info" | "neutral" {
  const value = label.toLowerCase();
  if (["ok", "connected", "configured", "production", "enabled", "active"].includes(value)) return "success";
  if (["disabled", "mock off", "none"].includes(value)) return "neutral";
  if (["missing", "misconfigured", "failed", "invalid", "error"].includes(value)) return "danger";
  if (["warning", "pending", "unknown", "preview"].includes(value)) return "warning";
  return "info";
}

function accountRoleLabel(role: OwnerAccount["managedRole"]): string {
  if (role === "owner") return "Owner";
  if (role === "promoter_organizer") return "Promoter / Organizer";
  if (role === "door_staff") return "Door Staff";
  return "Unassigned";
}

function routeForRole(role: ManagedAccountRole): string {
  if (role === "owner") return "/owner";
  if (role === "door_staff") return "/scanner";
  return "/app";
}

function statusLabel(enabled: boolean, onLabel: string, offLabel: string): string {
  return enabled ? onLabel : offLabel;
}

export default function OwnerDashboard({ navigate, path, workspaceNavigationAccess }: OwnerDashboardProps) {
  const section = useMemo(() => sectionForPath(path), [path]);
  const [status, setStatus] = useState<PlatformStatus>();
  const [diagnostics, setDiagnostics] = useState<OwnerDiagnostics>();
  const [webhooks, setWebhooks] = useState<OwnerWebhookEvent[]>([]);
  const [checkoutSessions, setCheckoutSessions] = useState<OwnerCheckoutSession[]>([]);
  const [emailDeliveries, setEmailDeliveries] = useState<OwnerEmailDelivery[]>([]);
  const [orders, setOrders] = useState<OwnerRecentOrder[]>([]);
  const [scannerActivity, setScannerActivity] = useState<OwnerScannerActivity[]>([]);
  const [reconciliation, setReconciliation] = useState<OwnerReconciliationReport>();
  const [accounts, setAccounts] = useState<OwnerAccount[]>([]);
  const [organizations, setOrganizations] = useState<OwnerOrganizationSummary[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<OwnerOrganizationDetail>();
  const [organizationForm, setOrganizationForm] = useState<OwnerOrganizationInput>({ name: "", slug: "", publicEmail: "", supportEmail: "", defaultCurrency: "usd" });
  const [memberDraft, setMemberDraft] = useState({ userId: "", role: "manager" as "owner" | "manager" | "door_staff" });
  const [accountForm, setAccountForm] = useState<OwnerAccountCreateInput>(defaultCreateAccount);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [roleDrafts, setRoleDrafts] = useState<Record<string, ManagedAccountRole>>({});
  const [ownerRoleConfirms, setOwnerRoleConfirms] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function loadHealth() {
    const [nextStatus, nextDiagnostics, nextWebhooks, nextCheckout, nextReconciliation] = await Promise.all([
      getOwnerPlatformStatus(),
      getOwnerDiagnostics(),
      listOwnerWebhooks(),
      listOwnerCheckoutSessions(),
      getOwnerReconciliation()
    ]);
    setStatus(nextStatus);
    setDiagnostics(nextDiagnostics);
    setWebhooks(nextWebhooks);
    setCheckoutSessions(nextCheckout);
    setReconciliation(nextReconciliation);
  }

  async function loadAccounts() {
    const nextAccounts = await listOwnerAccounts();
    setAccounts(nextAccounts);
    setRoleDrafts(
      Object.fromEntries(
        nextAccounts
          .filter((account) => account.managedRole !== "unassigned")
          .map((account) => [account.id, account.managedRole as ManagedAccountRole])
      )
    );
  }

  async function loadOrganizations() {
    setOrganizations(await listOwnerOrganizations());
  }

  async function loadOrders() {
    setOrders(await listOwnerRecentOrders());
  }

  async function loadEmailDeliveries() {
    setEmailDeliveries(await listOwnerEmailDeliveries());
  }

  async function loadScannerActivity() {
    setScannerActivity(await listOwnerScannerActivity());
  }

  useEffect(() => {
    setLoading(true);
    setError("");
    setMessage("");
    const task =
      section === "health"
        ? loadHealth()
        : section === "accounts"
          ? loadAccounts()
          : section === "organizations"
            ? Promise.all([loadOrganizations(), loadAccounts()]).then(() => undefined)
          : section === "orders"
            ? loadOrders()
            : section === "email-deliveries"
              ? loadEmailDeliveries()
              : section === "scanner-activity"
                ? loadScannerActivity()
                : Promise.resolve();

    task.catch((nextError) => {
      setError(nextError instanceof Error ? nextError.message : "Owner data could not be loaded.");
    }).finally(() => setLoading(false));
  }, [section]);

  async function run(action: () => Promise<void>, success: string) {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(success);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Action failed.");
    } finally {
      setLoading(false);
    }
  }

  async function submitNewAccount() {
    await run(async () => {
      const created = await createOwnerAccount(accountForm);
      setAccounts((current) => [...current, created]);
      setAccountForm(defaultCreateAccount);
      setRoleDrafts((current) => ({ ...current, [created.id]: created.managedRole === "unassigned" ? "promoter_organizer" : created.managedRole }));
    }, `Account created. Route this user to ${routeForRole(accountForm.role)} after they sign in.`);
  }

  async function saveAccountRole(account: OwnerAccount) {
    const nextRole = roleDrafts[account.id] ?? "promoter_organizer";
    await run(async () => {
      const updated = await updateOwnerAccount(account.id, {
        role: nextRole,
        confirmOwnerRoleChange: nextRole === "owner" ? ownerRoleConfirms[account.id] === true : undefined
      });
      setAccounts((current) => current.map((item) => (item.id === account.id ? updated : item)));
    }, "Account role updated.");
  }

  async function toggleAccount(account: OwnerAccount) {
    await run(async () => {
      const updated = await updateOwnerAccount(account.id, { isActive: !account.isActive });
      setAccounts((current) => current.map((item) => (item.id === account.id ? updated : item)));
    }, account.isActive ? "Account deactivated." : "Account activated.");
  }

  async function submitPasswordChange() {
    await run(async () => {
      await changePassword(passwordForm);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    }, "Password updated. Other active sessions for this account were revoked.");
  }

  async function selectOrganization(id: string) {
    await run(async () => setSelectedOrganization(await getOwnerOrganization(id)), "Organization loaded.");
  }

  async function submitOrganization() {
    await run(async () => {
      const created = await createOwnerOrganization(organizationForm);
      setOrganizationForm({ name: "", slug: "", publicEmail: "", supportEmail: "", defaultCurrency: "usd" });
      setOrganizations(await listOwnerOrganizations());
      setSelectedOrganization(await getOwnerOrganization(created.id));
    }, "Organization created.");
  }

  async function addOrganizationMember() {
    if (!selectedOrganization || !memberDraft.userId) return;
    await run(async () => {
      await setOwnerOrganizationMember(selectedOrganization.id, memberDraft.userId, memberDraft.role);
      setSelectedOrganization(await getOwnerOrganization(selectedOrganization.id));
      setAccounts(await listOwnerAccounts());
      setMemberDraft({ userId: "", role: "manager" });
    }, "Organization member saved.");
  }

  async function changeOrganizationMemberRole(userId: string, role: "owner" | "manager" | "door_staff") {
    if (!selectedOrganization) return;
    await run(async () => { await updateOwnerOrganizationMember(selectedOrganization.id, userId, role); setSelectedOrganization(await getOwnerOrganization(selectedOrganization.id)); setAccounts(await listOwnerAccounts()); }, "Organization role updated.");
  }

  async function removeOrganizationMember(userId: string) {
    if (!selectedOrganization) return;
    await run(async () => { await removeOwnerOrganizationMember(selectedOrganization.id, userId); setSelectedOrganization(await getOwnerOrganization(selectedOrganization.id)); setAccounts(await listOwnerAccounts()); }, "Organization membership removed.");
  }

  async function removeOrganization() {
    if (!selectedOrganization) return;
    let deactivatedEventCount = 0;
    await run(async () => {
      const result = await deleteOwnerOrganization(selectedOrganization.id);
      setOrganizations(await listOwnerOrganizations());
      setSelectedOrganization(undefined);
      deactivatedEventCount = result.deactivated ? result.eventCount : 0;
    }, "Organization deleted safely.");
    if (deactivatedEventCount) setMessage(`Organization deactivated safely; its ${deactivatedEventCount} events remain intact.`);
  }

  async function saveOrganizationIdentity() {
    if (!selectedOrganization) return;
    await run(async () => {
      const updated = await updateOwnerOrganization(selectedOrganization.id, {
        name: selectedOrganization.name,
        slug: selectedOrganization.slug,
        publicEmail: selectedOrganization.publicEmail,
        supportEmail: selectedOrganization.supportEmail,
        defaultCurrency: selectedOrganization.defaultCurrency,
        isActive: selectedOrganization.isActive
      });
      setOrganizations(await listOwnerOrganizations());
      setSelectedOrganization(await getOwnerOrganization(updated.id));
    }, "Organization identity updated.");
  }

  const statusCards = [
    ["Platform", status?.status ?? "unknown"],
    ["Runtime", diagnostics?.runtimeMode ?? status?.runtimeMode ?? "unknown"],
    ["D1 binding", statusLabel(diagnostics?.d1Available === true, "connected", "missing")],
    ["Session secret", statusLabel(diagnostics?.sessionConfigured === true, "configured", "missing")],
    ["Stripe", statusLabel(diagnostics?.stripeConfigured === true, "configured", "missing")],
    ["Stripe webhook", statusLabel(diagnostics?.stripeWebhookConfigured === true, "configured", "missing")],
    ["Email", diagnostics?.emailProviderMode ?? "unknown"],
    ["Mock data", diagnostics?.mockDataAllowed ? "allowed" : "disabled"]
  ] as const;

  const failedWebhooks = webhooks.filter((event) => event.status === "failed").slice(0, 5);
  const pendingSessions = checkoutSessions.filter((item) => ["reserved", "created", "failed"].includes(item.status)).slice(0, 6);

  function renderNav() {
    return (
      <div className="owner-nav">
        {navItems.map((item) => (
          <button
            key={item.path}
            type="button"
            className={path === item.path ? "owner-nav__item owner-nav__item-active" : "owner-nav__item"}
            onClick={() => navigate(item.path)}
          >
            {item.label}
          </button>
        ))}
        <button type="button" className="owner-nav__item" onClick={() => navigate("/app/events")}>
          Events
        </button>
      </div>
    );
  }

  function renderHealth() {
    return (
      <>
        <Panel title="System Health" description="Configuration checks grouped for a compact launch-readiness scan." compact>
          <div className="health-check-list">
            {statusCards.map(([label, value]) => (
              <div className="health-check-row" key={label}>
                <span>{label}</span>
                <StatusBadge label={value} tone={badgeTone(value)} />
              </div>
            ))}
          </div>
        </Panel>

        <div className="owner-layout-two">
          <Panel
            title="Launch Readiness"
            description="Production login is live. Keep launch changes explicit and test from unlisted events first."
            actions={status?.environment ? <StatusBadge label={status.environment} tone={badgeTone(status.environment)} /> : undefined}
          >
            <div className="stack-list stack-list-tight">
              <div className="detail-row">
                <StatusBadge
                  label={diagnostics?.productionLaunchReady ? "Ready" : "Needs attention"}
                  tone={diagnostics?.productionLaunchReady ? "success" : "warning"}
                />
                {diagnostics?.publicBaseUrl && <StatusBadge label={diagnostics.publicBaseUrl} tone="info" />}
              </div>
              {(diagnostics?.launchCriticalMissing.length ?? 0) === 0 ? (
                <p className="panel-copy">No blocking launch config gaps detected in owner diagnostics.</p>
              ) : (
                <div className="pill-list">
                  {diagnostics?.launchCriticalMissing.map((item) => (
                    <StatusBadge key={item} label={item} tone="danger" />
                  ))}
                </div>
              )}
              <div className="button-row">
                <button className="primary-action" type="button" onClick={() => navigate("/app/events/new")} disabled={loading}>
                  Create Unlisted Event Draft
                </button>
                <button className="secondary-action" type="button" onClick={() => navigate("/app/events")}>
                  Open Event Workspace
                </button>
              </div>
            </div>
          </Panel>

          <Panel title="Platform Metrics" description="A quick check before you release tickets or run scanner rehearsal.">
            <div className="metric-grid metric-grid-compact">
              <MetricCard label="Organizers" value={String(status?.metrics.organizers ?? 0)} />
              <MetricCard label="Live events" value={String(status?.metrics.liveEvents ?? status?.metrics.activeEvents ?? 0)} />
              <MetricCard label="Orders" value={String(status?.metrics.orders ?? 0)} />
              <MetricCard label="Checked in now" value={String(status?.metrics.checkedInNow ?? 0)} />
            </div>
          </Panel>
        </div>

        <div className="owner-layout-two">
          <Panel title="Webhook Failures" description="Keep this clear before you move from rehearsal to live traffic.">
            {(failedWebhooks.length ?? 0) === 0 ? (
              <p className="empty-state">No recent failed webhook events.</p>
            ) : (
              <div className="stack-list">
                {failedWebhooks.map((event) => (
                  <article className="mini-card" key={event.id}>
                    <div className="mini-card__header">
                      <strong>{event.eventType}</strong>
                      <StatusBadge label={event.status} tone={badgeTone(event.status)} />
                    </div>
                    <p>{formatDateTime(event.createdAt)}</p>
                    {event.errorMessage && <p>{event.errorMessage}</p>}
                  </article>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Checkout Exceptions" description="Reservations or failures that deserve a manual look.">
            {(pendingSessions.length ?? 0) === 0 ? (
              <p className="empty-state">No pending or failed checkout sessions.</p>
            ) : (
              <div className="stack-list">
                {pendingSessions.map((session) => (
                  <article className="mini-card" key={session.id}>
                    <div className="mini-card__header">
                      <strong>{session.eventTitle ?? session.eventId}</strong>
                      <StatusBadge label={session.status} tone={badgeTone(session.status)} />
                    </div>
                    <p>
                      {session.quantity} tickets · {currency.format(session.amountTotalCents / 100)} ·{" "}
                      {session.reservedUntil ?? session.expiresAt ?? "No expiry"}
                    </p>
                  </article>
                ))}
              </div>
            )}
          </Panel>
        </div>

        <Panel title="Reconciliation" description="Read-only cross-checks before you trust the night to production.">
          <div className="metric-grid metric-grid-compact">
            <MetricCard
              label="Missing orders"
              value={String(reconciliation?.checkoutSessionsCompletedMissingOrder.length ?? 0)}
              hint={<StatusBadge label={reconciliation?.adminReconciliationEnabled ? "Admin fixes enabled" : "Read only"} tone="info" />}
            />
            <MetricCard
              label="Ticket mismatches"
              value={String(reconciliation?.ordersWithTicketQuantityMismatch.length ?? 0)}
            />
            <MetricCard
              label="Expired pending"
              value={String(reconciliation?.expiredPendingCheckoutSessions.length ?? 0)}
            />
          </div>
        </Panel>
      </>
    );
  }

  function renderAccounts() {
    return (
      <div className="owner-layout-two owner-layout-top">
        <Panel title="Create Account" description="Create promoter or scanner access without opening public signup.">
          <div className="form-grid">
            <FormField label="Name">
              <input value={accountForm.name} onChange={(event) => setAccountForm({ ...accountForm, name: event.target.value })} />
            </FormField>
            <FormField label="Email">
              <input
                type="email"
                autoComplete="email"
                value={accountForm.email}
                onChange={(event) => setAccountForm({ ...accountForm, email: event.target.value })}
              />
            </FormField>
            <FormField label="Temporary password" hint="Minimum 12 characters. This is not stored in the UI after creation.">
              <input
                type="password"
                autoComplete="new-password"
                value={accountForm.temporaryPassword}
                onChange={(event) => setAccountForm({ ...accountForm, temporaryPassword: event.target.value })}
              />
            </FormField>
            <FormField label="Role">
              <select
                value={accountForm.role}
                onChange={(event) => setAccountForm({ ...accountForm, role: event.target.value as ManagedAccountRole })}
              >
                <option value="promoter_organizer">Promoter / Organizer</option>
                <option value="door_staff">Door Staff</option>
                <option value="owner">Owner</option>
              </select>
            </FormField>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={accountForm.isActive !== false}
                onChange={(event) => setAccountForm({ ...accountForm, isActive: event.target.checked })}
              />
              <span>Account active immediately</span>
            </label>
            {accountForm.role === "owner" && (
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={accountForm.confirmOwnerCreation === true}
                  onChange={(event) => setAccountForm({ ...accountForm, confirmOwnerCreation: event.target.checked })}
                />
                <span>I intend to create another owner account.</span>
              </label>
            )}
          </div>
          <div className="button-row">
            <button className="primary-action" type="button" onClick={submitNewAccount} disabled={loading}>
              Create Account
            </button>
          </div>
        </Panel>

        <Panel title="Accounts" description="Owners can activate, deactivate, and adjust access without exposing credentials.">
          {accounts.length === 0 ? (
            <p className="empty-state">No accounts found.</p>
          ) : (
            <div className="account-list">
              {accounts.map((account) => {
                const nextRole = roleDrafts[account.id] ?? (account.managedRole === "unassigned" ? "promoter_organizer" : account.managedRole);
                return (
                  <article className="mini-card account-card" key={account.id}>
                    <div className="mini-card__header">
                      <div>
                        <strong>{account.displayName ?? account.email}</strong>
                        <p>{account.email}</p>
                      </div>
                      <div className="detail-row">
                        <StatusBadge label={accountRoleLabel(account.managedRole)} tone={account.managedRole === "owner" ? "warning" : "info"} />
                        <StatusBadge label={account.isActive ? "Active" : "Inactive"} tone={account.isActive ? "success" : "neutral"} />
                      </div>
                    </div>
                    <p>
                      {account.memberships?.length
                        ? account.memberships.map((membership) => `${membership.organizerName} — ${membership.role === "manager" ? "Manager" : membership.role === "door_staff" ? "Door Staff" : "Owner"}`).join(" · ")
                        : "No organizations linked"} · Last login {formatDateTime(account.lastLoginAt)}
                    </p>
                    <div className="form-grid account-card__controls">
                      <FormField label="Access role">
                        <select
                          value={nextRole}
                          onChange={(event) =>
                            setRoleDrafts((current) => ({ ...current, [account.id]: event.target.value as ManagedAccountRole }))
                          }
                        >
                          <option value="promoter_organizer">Promoter / Organizer</option>
                          <option value="door_staff">Door Staff</option>
                          <option value="owner">Owner</option>
                        </select>
                      </FormField>
                      {nextRole === "owner" && nextRole !== account.managedRole && (
                        <label className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={ownerRoleConfirms[account.id] === true}
                            onChange={(event) =>
                              setOwnerRoleConfirms((current) => ({ ...current, [account.id]: event.target.checked }))
                            }
                          />
                          <span>Confirm owner access</span>
                        </label>
                      )}
                    </div>
                    <div className="button-row">
                      <button className="secondary-action" type="button" onClick={() => saveAccountRole(account)} disabled={loading}>
                        Save Role
                      </button>
                      <button className="secondary-action" type="button" onClick={() => toggleAccount(account)} disabled={loading}>
                        {account.isActive ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    );
  }

  function renderOrders() {
    return (
      <Panel title="Recent Orders" description="A clean owner view for payment and ticket-delivery spot checks.">
        {orders.length === 0 ? (
          <p className="empty-state">No orders visible yet.</p>
        ) : (
          <div className="stack-list">
            {orders.map((order) => (
              <article className="mini-card" key={order.id}>
                <div className="mini-card__header">
                  <strong>{order.eventTitle ?? order.eventId}</strong>
                  <StatusBadge label={order.status} tone={badgeTone(order.status)} />
                </div>
                <p>
                  {order.ticketQuantity} tickets · {currency.format(order.totalCents / 100)} · Delivery{" "}
                  {order.ticketDeliveryStatus ?? "pending"}
                </p>
                <p>
                  {order.buyerName ?? order.buyerEmail} / {order.buyerPhone ?? "No phone"} / Email marketing{" "}
                  {order.marketingEmailOptIn ? "Opted in" : "Not opted in"} / SMS marketing{" "}
                  {order.marketingSmsOptIn ? "Opted in" : "Not opted in"}
                </p>
                <p>Created {formatDateTime(order.createdAt)}</p>
              </article>
            ))}
          </div>
        )}
      </Panel>
    );
  }

  function renderOrganizations() {
    return (
      <div className="owner-layout-two owner-layout-top">
        <Panel title="Create Organization" description="Create the public host identity used by events.">
          <div className="form-grid">
            <FormField label="Name"><input value={organizationForm.name} onChange={(event) => setOrganizationForm({ ...organizationForm, name: event.target.value })} /></FormField>
            <FormField label="Slug" hint="Generated from the name when left blank."><input value={organizationForm.slug ?? ""} onChange={(event) => setOrganizationForm({ ...organizationForm, slug: event.target.value })} /></FormField>
            <FormField label="Public email"><input type="email" value={organizationForm.publicEmail ?? ""} onChange={(event) => setOrganizationForm({ ...organizationForm, publicEmail: event.target.value })} /></FormField>
            <FormField label="Support email"><input type="email" value={organizationForm.supportEmail ?? ""} onChange={(event) => setOrganizationForm({ ...organizationForm, supportEmail: event.target.value })} /></FormField>
          </div>
          <div className="button-row"><button className="primary-action" type="button" onClick={submitOrganization} disabled={loading}>Create Organization</button></div>
        </Panel>
        <Panel title="Organizations" description="Member and event counts are aggregated in the owner API.">
          <div className="stack-list stack-list-tight">
            {organizations.map((organization) => <div className="organization-list-row" key={organization.id}>
              <div><strong>{organization.name}</strong><small>{organization.memberCount} members · {organization.eventCount} events</small></div>
              <div className="detail-row"><StatusBadge label={organization.isActive ? "Active" : "Inactive"} tone={organization.isActive ? "success" : "neutral"} /><button className="secondary-action" type="button" onClick={() => void selectOrganization(organization.id)}>Manage</button></div>
            </div>)}
            {organizations.length === 0 && <p className="empty-state">No organizations yet.</p>}
          </div>
        </Panel>
        {selectedOrganization && <Panel title={selectedOrganization.name} description={`/${selectedOrganization.slug} · ${selectedOrganization.isActive ? "Active" : "Inactive"}`}>
          <div className="owner-organization-detail">
            <section><h3>Identity</h3><div className="form-grid"><FormField label="Name"><input value={selectedOrganization.name} onChange={(event) => setSelectedOrganization({ ...selectedOrganization, name: event.target.value })} /></FormField><FormField label="Slug"><input value={selectedOrganization.slug} onChange={(event) => setSelectedOrganization({ ...selectedOrganization, slug: event.target.value })} /></FormField><FormField label="Public email"><input type="email" value={selectedOrganization.publicEmail ?? ""} onChange={(event) => setSelectedOrganization({ ...selectedOrganization, publicEmail: event.target.value })} /></FormField><FormField label="Support email"><input type="email" value={selectedOrganization.supportEmail ?? ""} onChange={(event) => setSelectedOrganization({ ...selectedOrganization, supportEmail: event.target.value })} /></FormField></div><button className="secondary-action" type="button" onClick={() => void saveOrganizationIdentity()} disabled={loading}>Save Identity</button></section>
            <section><h3>Members</h3><div className="stack-list stack-list-tight">{selectedOrganization.members.map((member) => <div className="organization-list-row" key={member.userId}><div><strong>{member.displayName ?? member.email}</strong><small>{member.email}</small></div><div className="detail-row"><select aria-label={`${member.email} role`} value={member.role} onChange={(event) => void changeOrganizationMemberRole(member.userId, event.target.value as "owner" | "manager" | "door_staff")}><option value="owner">Owner</option><option value="manager">Manager</option><option value="door_staff">Door staff</option></select><button className="secondary-action" type="button" onClick={() => void removeOrganizationMember(member.userId)}>Remove</button></div></div>)}</div></section>
            <section><h3>Add Member</h3><div className="form-grid"><FormField label="Account"><select value={memberDraft.userId} onChange={(event) => setMemberDraft({ ...memberDraft, userId: event.target.value })}><option value="">Select account</option>{accounts.filter((account) => !selectedOrganization.members.some((member) => member.userId === account.id)).map((account) => <option key={account.id} value={account.id}>{account.displayName ?? account.email}</option>)}</select></FormField><FormField label="Role"><select value={memberDraft.role} onChange={(event) => setMemberDraft({ ...memberDraft, role: event.target.value as "owner" | "manager" | "door_staff" })}><option value="manager">Manager</option><option value="door_staff">Door staff</option><option value="owner">Owner</option></select></FormField></div><button className="secondary-action" type="button" onClick={() => void addOrganizationMember()} disabled={loading || !memberDraft.userId}>Add Member</button></section>
            <section><h3>Events</h3>{selectedOrganization.events.length ? <div className="stack-list stack-list-tight">{selectedOrganization.events.map((event) => <div className="organization-list-row" key={event.id}><span>{event.title}<small>{formatDateTime(event.startsAt)}</small></span><StatusBadge label={event.status} tone={badgeTone(event.status)} /></div>)}</div> : <p className="empty-state">No events assigned.</p>}</section>
            <button className="secondary-action" type="button" onClick={() => void removeOrganization()} disabled={loading}>Delete Organization</button>
          </div>
        </Panel>}
      </div>
    );
  }

  function renderEmailDeliveries() {
    return (
      <Panel title="Email Deliveries" description="Email is still optional in production, so this view stays plainly diagnostic.">
        {emailDeliveries.length === 0 ? (
          <p className="empty-state">No email delivery records yet.</p>
        ) : (
          <div className="stack-list">
            {emailDeliveries.map((delivery) => (
              <article className="mini-card" key={delivery.id}>
                <div className="mini-card__header">
                  <strong>{delivery.eventTitle ?? delivery.emailType}</strong>
                  <StatusBadge label={delivery.status} tone={badgeTone(delivery.status)} />
                </div>
                <p>
                  Provider {delivery.provider ?? "none"} · Attempt {delivery.attemptNumber ?? 1} · {formatDateTime(delivery.createdAt)}
                </p>
                {delivery.failureReason && <p>{delivery.failureReason}</p>}
              </article>
            ))}
          </div>
        )}
      </Panel>
    );
  }

  function renderScannerActivity() {
    return (
      <Panel title="Scanner Activity" description="Recent exceptions and unusual scans across the platform.">
        {scannerActivity.length === 0 ? (
          <p className="empty-state">No scanner activity captured yet.</p>
        ) : (
          <div className="stack-list">
            {scannerActivity.map((activity) => (
              <article className="mini-card" key={activity.id}>
                <div className="mini-card__header">
                  <strong>{activity.eventTitle ?? activity.eventId}</strong>
                  <StatusBadge label={activity.result} tone={badgeTone(activity.result)} />
                </div>
                <p>
                  {activity.action} · {activity.source ?? "manual"} · {formatDateTime(activity.createdAt)}
                </p>
                {activity.ticketCodeSnapshot && <p>{activity.ticketCodeSnapshot}</p>}
              </article>
            ))}
          </div>
        )}
      </Panel>
    );
  }

  function renderChangePassword() {
    return (
      <div className="owner-layout-two owner-layout-top">
        <Panel title="Rotate Owner Password" description="Use this right after bootstrap, then any time access changes hands.">
          <div className="form-grid">
            <FormField label="Current password">
              <input
                type="password"
                autoComplete="current-password"
                value={passwordForm.currentPassword}
                onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })}
              />
            </FormField>
            <FormField label="New password" hint="Minimum 12 characters. Other active sessions will be revoked.">
              <input
                type="password"
                autoComplete="new-password"
                value={passwordForm.newPassword}
                onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })}
              />
            </FormField>
            <FormField label="Confirm new password">
              <input
                type="password"
                autoComplete="new-password"
                value={passwordForm.confirmPassword}
                onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })}
              />
            </FormField>
          </div>
          <div className="button-row">
            <button className="primary-action" type="button" onClick={submitPasswordChange} disabled={loading}>
              Update Password
            </button>
          </div>
        </Panel>

        <Panel title="Launch Checklist Shortcut" description="After password rotation, set up the first working staff loop.">
          <div className="stack-list">
            <p className="empty-state">1. Create promoter and door staff accounts.</p>
            <p className="empty-state">2. Build an unlisted event and add at least one active tier.</p>
            <p className="empty-state">3. Run Stripe test checkout and scanner rehearsal before live keys.</p>
          </div>
          <div className="button-row">
            <button className="secondary-action" type="button" onClick={() => navigate("/owner/accounts")}>
              Open Accounts
            </button>
            <button className="secondary-action" type="button" onClick={() => navigate("/app/events/new")}>
              Open Event Builder
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <PageShell
      eyebrow="Owner operations"
      title="AnTix Owner Console"
      description="Launch-readiness, account control, and ticketing checks for the Cloudflare-native production app."
      actions={<WorkspaceSwitcher path={path} access={workspaceNavigationAccess} navigate={navigate} />}
    >
      {renderNav()}
      {loading && <p className="panel-copy">Loading owner data...</p>}
      {message && <p className="form-success">{message}</p>}
      {error && <p className="form-error">{error}</p>}
      {section === "health" && renderHealth()}
      {section === "accounts" && renderAccounts()}
      {section === "organizations" && renderOrganizations()}
      {section === "orders" && renderOrders()}
      {section === "email-deliveries" && renderEmailDeliveries()}
      {section === "scanner-activity" && renderScannerActivity()}
      {section === "change-password" && renderChangePassword()}
    </PageShell>
  );
}
